import React from "react";
import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {fetchBillingTotals} from "@/lib/api";
import {activeEstimateFigure, finalizedFigure, freshnessNote, totalsState, UNKNOWN, type BillingFigure} from "@/lib/billingTotals";
import {BillingFigureDetail} from "@/components/BillingFigureDetail";
import {useStats} from "@/hooks/useStats";
import {useDashboardOrg} from "@/app/DashboardOrgContext";
import {Clock, ElectricityStack, Receipt, Users, ArrowRightCircle} from "@/components/icons";
import {Card, CardContent} from "@/components/ui/card";
import {formatNumber as formatLocalizedNumber} from "@voltie/localization-web";
import {useT} from "@/app/localization";

export default function ConsumptionsStat() {
    // Manager → org-scoped billing totals (/api/org/<id>/billing/totals); admin →
    // fleet. Key includes org + period so switching org never shows the prior org's
    // totals; scopeReady gates the manager pre-selection window.
    const {dashboardOrgId, scopeReady} = useDashboardOrg();
    const {data, isLoading, isError} = useQuery({
        queryKey: ["billing-totals", dashboardOrgId, "month"],
        queryFn: () => fetchBillingTotals('month', dashboardOrgId),
        enabled: scopeReady,
        refetchInterval: 60_000,
    });
    const stats = useStats();
    const t = useT();

    if (data?.no_units_assigned) return null;

    const activeDevices = stats.activeDevices;
    // Before the first published pass nothing is known: loading (or unavailable), never zeros. A failed
    // refresh keeps the last good totals on screen with a note instead of hiding them.
    const state = data ? totalsState(data) : null;
    const loading = isLoading || state === "calculating";
    const failed = (isError && data == null) || state === "unavailable";
    const ready = data && state === "ready" ? data : null;
    const note = ready ? freshnessNote(t, ready, isError) : null;
    const billed = ready ? finalizedFigure(t, ready) : null;
    const active = ready ? activeEstimateFigure(t, ready) : null;

    return (
        <Card>
            <CardContent className="station-overview-wrap py-4">
                <h6 className="text-base font-semibold">{t("operator.consumption.title")} <span className="text-xs font-normal text-slate-400">{t("operator.consumption.period")}</span></h6>
                {note && <div className="text-xs text-muted-foreground" data-testid="billing-freshness">{note}</div>}
                <div className="station-overview-container flex-wrap" data-testid="station-overview-tiles">
                    <StatCard
                        id="energy"
                        label={t("operator.consumption.energy")}
                        value={knownNumber(data?.total_energy_kwh, 2)}
                        unit="kWh"
                        color={"oklch(68.5% 0.169 237.323)"}
                        AccentIcon={ElectricityStack}
                        to="sessions"
                        activeDevices={activeDevices}
                        isLoading={loading}
                        isError={failed}
                    />
                    <StatCard
                        id="billed"
                        label={t("operator.consumption.billed")}
                        value={billed?.value ?? ""}
                        figure={billed}
                        AccentIcon={Receipt}
                        to="billing"
                        activeDevices={activeDevices}
                        isLoading={loading}
                        isError={failed}
                    />
                    <StatCard
                        id="active-estimate"
                        label={t("operator.consumption.active_estimate")}
                        value={active?.value ?? ""}
                        figure={active}
                        AccentIcon={Clock}
                        to="sessions"
                        activeDevices={activeDevices}
                        isLoading={loading}
                        isError={failed}
                    />
                    <StatCard
                        id="sessions"
                        label={t("operator.consumption.sessions")}
                        value={knownNumber(data?.total_sessions, 0)}
                        color={"oklch(70.4% 0.14 182.503)"}
                        AccentIcon={Users}
                        to="sessions"
                        activeDevices={activeDevices}
                        isLoading={loading}
                        isError={failed}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

/** Unknown (null) stays unknown, never a zero. */
function knownNumber(value: number | null | undefined, fractionDigits: number): string {
    return value != null && Number.isFinite(value) ? formatLocalizedNumber(value, fractionDigits) : UNKNOWN;
}

function StatCard({
    id,
    label,
    value,
    unit,
    figure,
    iconStrokeWidth=1.5,
    iconColor="var(--color-green)",
    color,
    AccentIcon,
    to,
    activeDevices,
    isLoading,
    isError,
}: {
    /** Stable id for the test hook; the label is translated text. */
    id: string;
    label: string;
    value: string;
    unit?: string;
    figure?: BillingFigure | null;
    iconStrokeWidth?: number,
    iconColor?: string,
    color?: string,
    AccentIcon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number; color?: string }>;
    to?: string;
    activeDevices: number;
    isLoading: boolean;
    isError: boolean;
}) {
    const t = useT();
    const arrow = (
        <div className="card-arrow-wrap">
            <div className="card-arrow-container">
                <span className="in-active-icon opacity-[.95]"><AccentIcon strokeWidth={iconStrokeWidth} color={"oklch(52% 0.105 223.128)"} size={38}/></span>
                <span className="active-icon opacity-[.95]" role="presentation"><ArrowRightCircle strokeWidth={iconStrokeWidth} color={"oklch(52% 0.105 223.128)"} size={38}/></span>
            </div>
        </div>
    );

    return (
        // A fixed minimum width (not the min-content of long amounts) lets the four tiles wrap;
        // the right padding keeps the label and value clear of the 80px corner arrow.
        <div className="each-card-container min-w-[13rem]" data-testid={`stat-${id}`}>
            <h3 className={"text-xs pr-[3.75rem]"}>{label}</h3>
            {isLoading ? (
                <h1><span className="animate-pulse" role="status" aria-label={t("common.state.loading")}>...</span></h1>
            ) : isError ? (
                <h1><span style={{color: "red", fontSize: 14}}>{t("operator.consumption.load_failed")}</span></h1>
            ) : (
                <>
                    <h1 className="pr-[3.75rem] [overflow-wrap:anywhere]" style={{color:color}}>{value} {unit && value !== UNKNOWN && <span>({unit})</span>}</h1>
                    {figure && <BillingFigureDetail figure={figure}/>}
                </>
            )}
            {to ? <Link to={to}>{arrow}</Link> : arrow}
        </div>
    );
}
